<script setup lang="ts">
import VCard from '~/components/ui/VCard.vue'

import type {
  InfluencerExpectationsData,
  InfluencerExpectationsTags,
} from '~/types/InfluencerExpectations'
import type { Locale } from '~/types/locale'

type Props = {
  expectationsData: InfluencerExpectationsData
}

const props = defineProps<Props>()
const expectationsTagNames = ['lyrics_lang', 'country', 'track_age'] as const

const { t, locale } = useI18n()

function isAny(tagNamesArray: string[]): boolean {
  const hasOneTagName = tagNamesArray?.length === 1
  const tagNameIsDunderAll = tagNamesArray[0] === '_all_'

  return hasOneTagName && tagNameIsDunderAll
}

function getIcon(tagType: keyof InfluencerExpectationsTags): string {
  if (props.expectationsData[tagType].isMismatch)
    return 'fa-solid fa-triangle-exclamation'

  switch (tagType) {
    case 'lyrics_lang':
      return 'fa-solid fa-microphone-lines'
    case 'country':
      return 'fa-solid fa-earth-americas'
    case 'track_age':
      return 'fa-regular fa-calendar-check'
  }
}

function getLyricsLangText(): string {
  const tags = props.expectationsData.lyrics_lang.tags
  let text = ''

  if (tags.length === 1 && tags[0] === '_none_') {
    text = t(
      'components.influencer.card.mismatches.lyrics_langInstrumental',
    ) as string
  } else {
    text = `
      ${t(`influencer.profile.slug.expectations.lyrics_lang.intro`)}
      ${getTagsList('lyrics_lang', tags)}
    `
  }

  return props.expectationsData.lyrics_lang.isMismatch
    ? `${text} ${t('common.only')}`
    : text
}

function getTagTargetKey() {
  switch (locale.value as Locale) {
    case 'de':
      return 'country'
    case 'fr':
    case 'en':
    default:
      return 'nationalities'
  }
}

function getNationalitiesText(): string {
  const tags = props.expectationsData.country.tags
  const nationalitiesList = tags.map((country: string) =>
    t(`tags.${getTagTargetKey()}.${country}`),
  )
  const nationalities = nationalitiesList
    .join(', ')
    .replace(/, ([^,]*)$/, ` ${t('common.and')} $1`)

  const text = t('influencer.profile.slug.expectations.artistTextOrder', {
    artists: t('common.artists'),
    nationalities,
  })

  return props.expectationsData.country.isMismatch
    ? `${text} ${t('common.only')}`
    : text
}

function getTagsList(tagType: string, tagNamesArray: string[]): string {
  const tagsList = tagNamesArray?.map((tagName) =>
    t(`tags.${tagType}.${tagName}`),
  )
  return (
    tagsList?.join(', ').replace(/, ([^,]*)$/, ` ${t('common.and')} $1`) || ''
  )
}
</script>

<template>
  <VCard class="tw-w-full" padding="5">
    <section>
      <h2 class="tw-text-h6">
        {{ t('influencer.profile.slug.expectations.title') }}
      </h2>
      <ul class="tw-mt-lg tw-flex tw-flex-col tw-gap-sm">
        <li
          v-for="tagType in expectationsTagNames"
          :key="tagType"
          class="tw-flex tw-items-center tw-gap-sm"
          :class="{
            'tw-text-error': expectationsData[tagType].isMismatch,
          }"
        >
          <i :class="getIcon(tagType)" aria-hidden></i>
          <span
            class="tw-text-body first-letter:tw-capitalize"
            :class="{
              '!tw-font-medium': expectationsData[tagType].isMismatch,
            }"
          >
            <span v-if="isAny(expectationsData[tagType].tags)">
              {{ t(`influencer.profile.slug.expectations.${tagType}.any`) }}
            </span>
            <span v-else-if="tagType === 'lyrics_lang'">
              {{ getLyricsLangText() }}
            </span>
            <span v-else-if="tagType === 'country'">
              {{ getNationalitiesText() }}
            </span>
            <span v-else-if="tagType === 'track_age'">
              {{
                t(
                  `influencer.profile.slug.expectations.track_age.${expectationsData['track_age'].tags[0]}`,
                )
              }}
            </span>
          </span>
        </li>
      </ul>
    </section>
  </VCard>
</template>
